import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { generateTimeSlots, getStartHour, hmmmToSlot, hmmmToEndSlot, isValidTimeRange } from "@/config/modeConfig";
import type { RosterGridData } from "@/types/api";
import { SWAP_MOVE_COLORS } from "@/config/swapMoveColors";
import { useDialogScale } from "@/hooks/useDialogScale";

interface CounterSwapMoveDialogProps {
  action: "swap" | "move";
  targetCounterNum: number;
  sourceLabel: string;
  targetLabel: string;
  shiftType: string;
  gridData: RosterGridData;
  onConfirm: (startTime: string, endTime: string, overwrite: boolean) => Promise<void>;
  onCancel: () => void;
  /** Called on every time-range change so the parent can update canvas highlights. */
  onTimeRangeChange: (startTime: string, endTime: string) => void;
  /** External time override — set when user taps a canvas cell while picker mode is active. */
  externalStartTime?: string;
  externalEndTime?: string;
  /** Which time field the user is currently picking via cell tap (null = no picker active). */
  activePicker?: "start" | "end" | null;
  /** Called by From/To buttons to activate or deactivate picker mode. */
  onPickerActivate?: (picker: "start" | "end" | null) => void;
}

function shortLabel(label: string) {
  return label.split("\n")[0];
}

export function CounterSwapMoveDialog({
  action,
  targetCounterNum,
  sourceLabel,
  targetLabel,
  shiftType,
  gridData,
  onConfirm,
  onCancel,
  onTimeRangeChange,
  externalStartTime,
  externalEndTime,
  activePicker,
  onPickerActivate,
}: CounterSwapMoveDialogProps) {
  const startHour = getStartHour(shiftType);
  const timeOpts = generateTimeSlots(shiftType);
  const shiftStart = timeOpts[0];
  const shiftEnd = timeOpts[timeOpts.length - 1];

  const [startTime, setStartTime] = useState(shiftStart);
  const [endTime, setEndTime] = useState(shiftEnd);
  const [overwrite, setOverwrite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Notify parent of initial range immediately on mount.
  useEffect(() => {
    onTimeRangeChange(shiftStart, shiftEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync internal state when parent sets an external time via canvas cell click.
  useEffect(() => {
    if (externalStartTime && externalStartTime !== startTime) {
      setStartTime(externalStartTime);
      setError(null);
      onTimeRangeChange(externalStartTime, endTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalStartTime]);

  useEffect(() => {
    if (externalEndTime && externalEndTime !== endTime) {
      setEndTime(externalEndTime);
      setError(null);
      onTimeRangeChange(startTime, externalEndTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalEndTime]);

  const fmtOpt = (t: string) => `${t.slice(0, 2)}:${t.slice(2)}`;

  const handleStartChange = (v: string) => {
    setStartTime(v);
    setError(null);
    onTimeRangeChange(v, endTime);
  };
  const handleEndChange = (v: string) => {
    setEndTime(v);
    setError(null);
    onTimeRangeChange(startTime, v);
  };

  // For "move": detect conflict on target counter in selected range.
  const targetHasOfficers = useMemo(() => {
    if (action !== "move") return false;
    if (!isValidTimeRange(startTime, endTime, shiftType)) return false;
    const startSlot = hmmmToSlot(startTime, startHour);
    const endSlot = hmmmToEndSlot(endTime, startHour);
    const targetRow = targetCounterNum - 1;
    for (let slot = startSlot; slot < endSlot; slot++) {
      const cell = gridData.cells[targetRow * gridData.num_slots + slot];
      if (cell?.officers && cell.officers.length > 0) return true;
    }
    return false;
  }, [action, startTime, endTime, shiftType, startHour, targetCounterNum, gridData]);

  const isTimeValid = isValidTimeRange(startTime, endTime, shiftType);
  const canConfirm = isTimeValid && (action === "swap" || !targetHasOfficers || overwrite);

  const handleConfirm = async () => {
    if (!isTimeValid) { setError("End time must be after start time."); return; }
    setSaving(true);
    setError(null);
    try {
      await onConfirm(startTime, endTime, overwrite);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Operation failed.");
      setSaving(false);
    }
  };

  const uiScale = useDialogScale();
  const colors = SWAP_MOVE_COLORS[action];
  const srcShort = shortLabel(sourceLabel);
  const tgtShort = shortLabel(targetLabel);
  const actionIcon = action === "swap" ? "⇄" : "→";
  const actionLabel = action === "swap" ? "Swap" : "Move";

  return createPortal(
    // No backdrop — just the floating card itself.
    <div
      style={{
        position: "fixed",
        bottom: 80,
        right: 16,
        width: 292,
        // Grow with the viewport: the card is laid out for a phone (292px), so on a
        // desktop it scales up as a whole (text, inputs, buttons) instead of staying tiny.
        zoom: uiScale,
        maxHeight: `${100 / uiScale}vh`,
        overflowY: "auto",
        background: "white",
        borderRadius: 12,
        boxShadow: "0 6px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.06)",
        zIndex: 4500,
        userSelect: "none",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Coloured top bar */}
      <div
        style={{
          height: 4,
          background:
            action === "swap"
              ? "linear-gradient(90deg, #86efac 50%, #10b981 100%)"
              : `linear-gradient(90deg, ${SWAP_MOVE_COLORS.move.source.border} 50%, ${SWAP_MOVE_COLORS.move.target.border} 100%)`,
        }}
      />

      <div style={{ padding: "12px 14px 14px" }}>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", flex: 1 }}>
            {actionLabel} counters
          </span>
          <button
            onClick={onCancel}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 17, color: "#94a3b8", lineHeight: 1, padding: "0 2px" }}
            title="Cancel"
          >×</button>
        </div>

        {/* Counter pair row with directional color coding */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            padding: "8px 10px",
            background: "#f8fafc",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
          }}
        >
          {/* Source chip */}
          <span
            style={{
              padding: "4px 10px",
              borderRadius: 99,
              background: colors.source.chip,
              border: `1.5px solid ${colors.source.border}`,
              fontSize: 12,
              fontWeight: 700,
              color: colors.source.label,
              fontFamily: "monospace, system-ui",
              flexShrink: 0,
            }}
          >
            {srcShort}
          </span>

          {/* Direction indicator */}
          <span style={{ fontSize: 15, color: colors.accent, fontWeight: 700, flexShrink: 0 }}>
            {actionIcon}
          </span>

          {/* Target chip */}
          <span
            style={{
              padding: "4px 10px",
              borderRadius: 99,
              background: colors.target.chip,
              border: `1.5px solid ${colors.target.border}`,
              fontSize: 12,
              fontWeight: 700,
              color: colors.target.label,
              fontFamily: "monospace, system-ui",
              flexShrink: 0,
            }}
          >
            {tgtShort}
          </span>

          {/* Colour legend dots */}
          <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-end" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: colors.source.label }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: colors.source.border, display: "inline-block", flexShrink: 0 }} />
              {action === "move" ? "from" : "A"}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: colors.target.label }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: colors.target.border, display: "inline-block", flexShrink: 0 }} />
              {action === "move" ? "to" : "B"}
            </span>
          </div>
        </div>

        {/* Time range — picker instruction + From/To buttons + dropdowns */}
        <div style={{ marginBottom: 4 }}>
          {/* Instruction above rows — neutral gray, shifts to actionable hint when picker active */}
          <p style={{
            fontSize: 10,
            color: activePicker ? "#374151" : "#94a3b8",
            fontWeight: activePicker ? 600 : 400,
            margin: "0 0 6px",
            lineHeight: 1.4,
            textAlign: "center",
          }}>
            {activePicker === "start"
              ? "👆 Tap any highlighted row cell to set FROM time"
              : activePicker === "end"
              ? "👆 Tap any highlighted row cell to set TO time"
              : "Tap From or To, then tap a cell on the grid above"}
          </p>
          {/* Start time row */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
            <button
              onClick={() => onPickerActivate?.(activePicker === "start" ? null : "start")}
              style={{
                padding: "4px 6px",
                border: `${activePicker === "start" ? 2 : 1.5}px solid ${activePicker === "start" ? colors.source.border : "#d1d5db"}`,
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 800,
                background: activePicker === "start" ? colors.source.chip : "#f9fafb",
                color: activePicker === "start" ? colors.source.label : "#6b7280",
                cursor: "pointer",
                flexShrink: 0,
                minWidth: 44,
                textAlign: "center",
                lineHeight: 1,
                transition: "all 0.15s",
                boxShadow: activePicker === "start" ? `0 0 0 3px ${colors.source.chipBorder}` : "none",
                whiteSpace: "nowrap",
              }}
            >
              {activePicker === "start" ? "From 👆" : "From"}
            </button>
            <select
              value={startTime}
              onChange={(e) => handleStartChange(e.target.value)}
              style={{ flex: 1, padding: "5px 4px", border: `1.5px solid ${colors.source.border}`, borderRadius: 6, fontSize: 12, background: colors.source.chip }}
            >
              {timeOpts.slice(0, -1).map((t) => (
                <option key={t} value={t}>{fmtOpt(t)}</option>
              ))}
            </select>
          </div>
          {/* End time row */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              onClick={() => onPickerActivate?.(activePicker === "end" ? null : "end")}
              style={{
                padding: "4px 6px",
                border: `${activePicker === "end" ? 2 : 1.5}px solid ${activePicker === "end" ? colors.target.border : "#d1d5db"}`,
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 800,
                background: activePicker === "end" ? colors.target.chip : "#f9fafb",
                color: activePicker === "end" ? colors.target.label : "#6b7280",
                cursor: "pointer",
                flexShrink: 0,
                minWidth: 44,
                textAlign: "center",
                lineHeight: 1,
                transition: "all 0.15s",
                boxShadow: activePicker === "end" ? `0 0 0 3px ${colors.target.chipBorder}` : "none",
                whiteSpace: "nowrap",
              }}
            >
              {activePicker === "end" ? "To 👆" : "To"}
            </button>
            <select
              value={endTime}
              onChange={(e) => handleEndChange(e.target.value)}
              style={{ flex: 1, padding: "5px 4px", border: `1.5px solid ${colors.target.border}`, borderRadius: 6, fontSize: 12, background: colors.target.chip }}
            >
              {timeOpts.slice(1).map((t) => (
                <option key={t} value={t}>{fmtOpt(t)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Conflict warning for move (compact) */}
        {action === "move" && targetHasOfficers && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 7,
              padding: "8px 10px",
              marginBottom: 10,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: "#991b1b", marginBottom: 4 }}>
              ⚠️ {tgtShort} has officers here
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                style={{ width: 13, height: 13, accentColor: "#dc2626", cursor: "pointer" }}
              />
              <span style={{ fontSize: 11, fontWeight: 600, color: "#7f1d1d" }}>
                Overwrite {tgtShort} in this range
              </span>
            </label>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ fontSize: 11, color: "#dc2626", marginBottom: 8 }}>{error}</div>
        )}

        {/* Hint text */}
        {!error && (
          <p style={{ fontSize: 10, color: "#94a3b8", margin: "0 0 10px", lineHeight: 1.4 }}>
            {action === "swap"
              ? "Officers on both counters trade places for the selected range."
              : "Officers on the source counter are reassigned to the destination."}
            {" "}Highlighted on the grid above.
          </p>
        )}

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: "8px 0", background: "#f1f5f9", border: "1px solid #e2e8f0",
              borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#374151",
            }}
          >
            Cancel
          </button>
          <button
            disabled={saving || !canConfirm}
            onClick={() => void handleConfirm()}
            style={{
              flex: 1, padding: "8px 0",
              background: canConfirm ? colors.accent : "#cbd5e1",
              border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700,
              cursor: canConfirm ? "pointer" : "not-allowed", color: "white",
              transition: "background 0.15s",
            }}
          >
            {saving ? "…" : `${actionLabel} ${actionIcon}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
